import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import { Markdown } from "@tiptap/markdown";
import { WikiLink } from "@/components/extensions/WikiLink";
import {
  Bold, Italic, Strikethrough, Code, Heading1, Heading2, Heading3, Type,
  List, ListOrdered, ListChecks, Quote, Link2, Undo2, Redo2, Minus,
} from "lucide-react";

/**
 * Visual (WYSIWYG) note editor for non-technical users.
 * Reads & writes the vault's Markdown under the hood — so [[wiki-links]], the
 * graph and search keep working — but the person edits a richly-formatted page
 * with no ## or ** syntax. YAML frontmatter (properties) is preserved untouched.
 * Type `[[` to link another note via the inline autocomplete.
 */
function splitFrontmatter(md) {
  const m = (md || "").match(/^(---\n[\s\S]*?\n---\n?)/);
  if (m) return { fm: m[1], body: (md || "").slice(m[1].length) };
  return { fm: "", body: md || "" };
}

function getMarkdown(editor) {
  if (typeof editor.getMarkdown === "function") return editor.getMarkdown();
  const mgr = editor.storage?.markdown?.manager;
  if (mgr) return mgr.serialize(editor.getJSON());
  return editor.getText();
}

export default function RichEditor({ value, onChange, docs = [], onOpenDoc, placeholder = "Start writing…" }) {
  const fmRef = useRef(splitFrontmatter(value).fm);
  const onChangeRef = useRef(onChange);
  const docsRef = useRef(docs);
  const onOpenRef = useRef(onOpenDoc);

  const containerRef = useRef(null);
  const cbRef = useRef({});
  const acRef = useRef(null);
  const [ac, setAc] = useState(null);

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener" } },
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Placeholder.configure({ placeholder }),
      Markdown,
      WikiLink,
    ],
    content: splitFrontmatter(value).body,
    contentType: "markdown",
    editorProps: {
      handleKeyDown: (_view, event) => cbRef.current.onKeyDown?.(event) || false,
      handleClickOn: (_view, _pos, node) => cbRef.current.onClickNode?.(node) || false,
    },
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current(fmRef.current + getMarkdown(ed));
    },
  });

  // ----- live callbacks (kept fresh each render via ref) -----
  // Detect the active "[[query" context at the (collapsed) cursor. Used for BOTH
  // the autocomplete display AND, re-derived fresh, the insertion — so a turn is
  // always anchored to the text currently under the cursor, never a stale range.
  const wikiCtxAt = () => {
    if (!editor) return null;
    const sel = editor.state.selection;
    if (!sel.empty) return null;
    const { $from } = sel;
    const offset = $from.parentOffset;
    const before = $from.parent.textBetween(0, offset, "\n", "\ufffc");
    const open = before.lastIndexOf("[[");
    if (open === -1) return null;
    const between = before.slice(open + 2);
    if (/[[\]\n]/.test(between)) return null;
    return { from: $from.pos - (before.length - open), to: $from.pos, query: between.toLowerCase().trim() };
  };

  const rankDocs = (q) => {
    const matches = (docsRef.current || []).filter((d) => d.name && d.name.toLowerCase().includes(q));
    const idxOf = (name) => name.toLowerCase().indexOf(q);
    return matches
      .sort((a, b) => {
        const ra = a.name.toLowerCase().startsWith(q) ? 0 : 1;
        const rb = b.name.toLowerCase().startsWith(q) ? 0 : 1;
        if (ra !== rb) return ra - rb;
        return idxOf(a.name) - idxOf(b.name) || a.name.length - b.name.length;
      })
      .slice(0, 8);
  };

  const recompute = () => {
    if (!editor || !containerRef.current) return;
    const ctx = wikiCtxAt();
    if (!ctx) { setAc(null); return; }
    const options = rankDocs(ctx.query);
    if (!options.length) { setAc(null); return; }
    let coords;
    try { coords = editor.view.coordsAtPos(ctx.from); } catch { setAc(null); return; }
    const rect = containerRef.current.getBoundingClientRect();
    setAc({
      options,
      index: 0,
      top: coords.bottom - rect.top + 4,
      left: Math.max(4, coords.left - rect.left),
    });
  };

  const selectOption = (doc) => {
    if (!editor) return;
    // Re-derive the wiki context from the LIVE editor state so we always replace
    // the text currently under the cursor — never a stale stored range. If the
    // clicked/Enter-selected option is stale (a fast interaction landed on
    // options from a previous query), self-heal to the best match for the
    // CURRENT query so we never insert a note that doesn't match what's typed.
    const ctx = wikiCtxAt();
    if (!ctx) { setAc(null); return; }
    const fresh = rankDocs(ctx.query);
    const pick = doc && fresh.some((d) => d.id === doc.id) ? doc : fresh[0];
    if (!pick) { setAc(null); return; }
    editor
      .chain()
      .focus()
      .insertContentAt({ from: ctx.from, to: ctx.to }, [
        { type: "wikiLink", attrs: { target: pick.name } },
        { type: "text", text: " " },
      ])
      .run();
    setAc(null);
  };

  const onKeyDown = (event) => {
    const a = acRef.current;
    if (!a) return false;
    if (event.key === "ArrowDown") { setAc((s) => (s ? { ...s, index: (s.index + 1) % s.options.length } : s)); return true; }
    if (event.key === "ArrowUp") { setAc((s) => (s ? { ...s, index: (s.index - 1 + s.options.length) % s.options.length } : s)); return true; }
    if (event.key === "Enter" || event.key === "Tab") { selectOption(a.options[a.index]); return true; }
    if (event.key === "Escape") { setAc(null); return true; }
    return false;
  };

  const onClickNode = (node) => {
    if (node?.type?.name !== "wikiLink") return false;
    const name = node.attrs.target;
    const found = (docsRef.current || []).find(
      (d) => d.name && d.name.toLowerCase() === String(name).toLowerCase()
    );
    if (found && onOpenRef.current) { onOpenRef.current(found.id); return true; }
    return false;
  };

  // Keep latest props/state in refs that editor callbacks read. Done in an
  // effect (after commit) — not during render — per react-hooks/refs. These
  // refs are only read inside event-driven callbacks (onUpdate, handleKeyDown,
  // the transaction handler) that always fire after the commit completes.
  useEffect(() => {
    fmRef.current = splitFrontmatter(value).fm;
    onChangeRef.current = onChange;
    docsRef.current = docs;
    onOpenRef.current = onOpenDoc;
    acRef.current = ac;
    cbRef.current = { recompute, onKeyDown, onClickNode };
  });

  // Re-render the toolbar on every transaction (active states) + recompute autocomplete.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!editor) return undefined;
    const handler = () => { setTick((t) => t + 1); cbRef.current.recompute?.(); };
    editor.on("transaction", handler);
    return () => editor.off("transaction", handler);
  }, [editor]);

  if (!editor) return <div className="rich-editor" ref={containerRef} data-testid="rich-editor" />;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="rich-editor" ref={containerRef} data-testid="rich-editor">
      <div className="rich-toolbar" data-testid="rich-toolbar">
        <Group>
          <Btn onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive("paragraph")} title="Body text"><Type size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1" testid="rt-h1"><Heading1 size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2" testid="rt-h2"><Heading2 size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3"><Heading3 size={15} /></Btn>
        </Group>
        <Divider />
        <Group>
          <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold (⌘B)" testid="rt-bold"><Bold size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic (⌘I)" testid="rt-italic"><Italic size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Strikethrough"><Strikethrough size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive("code")} title="Inline code"><Code size={15} /></Btn>
          <Btn onClick={setLink} active={editor.isActive("link")} title="Link" testid="rt-link"><Link2 size={15} /></Btn>
        </Group>
        <Divider />
        <Group>
          <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bulleted list" testid="rt-bullet"><List size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><ListOrdered size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleTaskList().run()} active={editor.isActive("taskList")} title="Checklist" testid="rt-task"><ListChecks size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Quote"><Quote size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider"><Minus size={15} /></Btn>
        </Group>
        <Divider />
        <Group>
          <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo2 size={15} /></Btn>
          <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo2 size={15} /></Btn>
        </Group>
      </div>
      <EditorContent editor={editor} className="rich-content" data-testid="rich-content" />
      {ac && (
        <div className="rich-ac" style={{ top: ac.top, left: ac.left }} data-testid="rich-wikilink-autocomplete">
          <div className="rich-ac-head">Link to note</div>
          {ac.options.map((d, i) => (
            <button
              key={d.id}
              type="button"
              className={`rich-ac-item ${i === ac.index ? "active" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); selectOption(d); }}
              data-testid={`rich-wikilink-option-${i}`}
            >
              <span className="rich-ac-name">{d.name}</span>
              {d.folder ? <span className="rich-ac-folder">{d.folder}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Group({ children }) {
  return <div className="rt-group">{children}</div>;
}
function Divider() {
  return <span className="rt-divider" />;
}
function Btn({ onClick, active, title, children, testid }) {
  return (
    <button
      type="button"
      className={`rt-btn ${active ? "active" : ""}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
